// Docs helpers: a props table generated from a component's zod schema (nested fields
// included), and a variant builder that swaps one enum prop in an example. Reads zod v3
// internals (`_def`) only through the small unwrap/typeOf below; no I/O.
import YAML from "yaml";
import { isTextComponent } from "./schemas.js";
import { serializeInfoString } from "./infostring.js";
import { scan, flattenItems } from "./scan.js";
/** Peel optional/default/effects layers, keeping the description and default it was given. */
function unwrap(schema) {
    let cur = schema;
    let required = true;
    let description;
    let defaultValue;
    for (;;) {
        const def = cur._def;
        const desc = cur.description;
        if (desc && !description)
            description = desc;
        switch (def.typeName) {
            case "ZodOptional":
                required = false;
                cur = def.innerType;
                continue;
            case "ZodNullable":
                cur = def.innerType;
                continue;
            case "ZodDefault":
                required = false;
                defaultValue = def.defaultValue();
                cur = def.innerType;
                continue;
            case "ZodEffects":
                // transforms and preprocess (e.g. the stat tone aliases): document the inner type
                cur = def.schema;
                continue;
            default:
                return { schema: cur, required, description, defaultValue };
        }
    }
}
/** The object schema behind a value, plus the path infix its fields get. */
function nestedObject(schema) {
    const unwrapped = unwrap(schema).schema;
    if (unwrapped._def.typeName === "ZodObject")
        return { shape: unwrapped, infix: "." };
    if (unwrapped._def.typeName === "ZodArray") {
        const el = unwrap(unwrapped._def.type).schema;
        if (el._def.typeName === "ZodObject")
            return { shape: el, infix: "[]." };
    }
    return null;
}
const MAX_DEPTH = 3;
function typeOf(schema) {
    const def = schema._def;
    switch (def.typeName) {
        case "ZodString":
            return { text: "string" };
        case "ZodNumber":
            return { text: "number" };
        case "ZodBoolean":
            return { text: "boolean" };
        case "ZodEnum":
            return { text: def.values.join(" | "), enumValues: [...def.values] };
        case "ZodLiteral":
            // keep the literal's real type (2, not "2") so YAML variants stay unquoted
            return { text: JSON.stringify(def.value), enumValues: [def.value] };
        case "ZodUnion": {
            const parts = def.options.map(typeOf);
            if (parts.every((p) => p.enumValues)) {
                const values = parts.flatMap((p) => p.enumValues);
                return { text: values.map(String).join(" | "), enumValues: values };
            }
            // YAML scalar (string | number | boolean, stringified by a transform): document it as a string.
            if (parts.every((p) => p.text === "string" || p.text === "number" || p.text === "boolean"))
                return { text: "string" };
            return { text: parts.map((p) => p.text).join(" | ") };
        }
        case "ZodArray": {
            const el = typeOf(def.type);
            return { text: /\s\|\s/.test(el.text) ? `(${el.text})[]` : `${el.text}[]` };
        }
        case "ZodObject": {
            // inline form shows each field's type so enum values are never hidden behind `{...}`
            const shape = def.shape();
            const keys = Object.entries(shape).map(([k, v]) => {
                const u = unwrap(v);
                const t = typeOf(u.schema);
                return `${k}${u.required ? "" : "?"}:${t.text.replaceAll(" | ", "|")}`;
            });
            return { text: `{${keys.join(", ")}}` };
        }
        case "ZodEffects":
            return typeOf(def.schema);
        default:
            return { text: "value" };
    }
}
/**
 * Rows for a docs table: one per schema key, in schema order, plus one row per field of
 * every object (or array-of-object) value — named `items[].status` — so nested enums and
 * their descriptions come from the schema and cannot drift from a hand-written list.
 */
export function propsTable(schema) {
    const rows = [];
    const collect = (objectSchema, prefix, depth) => {
        const def = unwrap(objectSchema).schema._def;
        if (def.typeName !== "ZodObject")
            return;
        const shape = def.shape();
        for (const [key, prop] of Object.entries(shape)) {
            const u = unwrap(prop);
            const t = typeOf(u.schema);
            const name = `${prefix}${key}`;
            const row = { name, depth, type: t.text, required: u.required };
            if (t.enumValues)
                row.enum = t.enumValues;
            if (u.description)
                row.description = u.description;
            if (u.defaultValue !== undefined) {
                row.default = typeof u.defaultValue === "string" ? u.defaultValue : JSON.stringify(u.defaultValue);
            }
            rows.push(row);
            const nested = depth < MAX_DEPTH ? nestedObject(prop) : null;
            if (nested)
                collect(nested.shape, `${name}${nested.infix}`, depth + 1);
        }
    };
    collect(schema, "", 0);
    return rows;
}
/** Root prop a (possibly nested) row name belongs to — for filtering derived props. */
export function rootProp(name) {
    return name.split(/[.[]/)[0];
}
/**
 * Copy of `markdown`'s first component fence with top-level `prop` set to `value` — used
 * for the docs variant strips (tone, chart type, ...). Values keep their type: numbers
 * stay unquoted in YAML and info strings. Returns null when the swap does not apply
 * (nested paths like `items[].status`, or a `columns` body that is not YAML).
 */
export function variantExample(markdown, prop, value) {
    if (prop.includes(".") || prop.includes("["))
        return null;
    const item = flattenItems(scan(markdown)).find((i) => i.kind === "component");
    if (!item || item.kind !== "component" || item.name === "columns")
        return null;
    const raw = markdown.slice(item.start, item.end);
    const nl = raw.indexOf("\n");
    if (nl < 0)
        return null;
    const fence = /^([ \t]{0,3})(`+|~+)/.exec(raw)?.[2];
    if (!fence)
        return null;
    const body = item.body.replace(/\s+$/, "");
    if (isTextComponent(item.name)) {
        const attrs = { ...item.info.attrs, [prop]: value };
        return `${fence}${serializeInfoString(item.name, attrs)}\n${body}\n${fence}`;
    }
    let data;
    try {
        const parsed = YAML.parse(item.body);
        if (parsed !== null && typeof parsed !== "object")
            return null;
        data = (parsed ?? {});
    }
    catch {
        return null;
    }
    data[prop] = value;
    return `${fence}${item.name}\n${YAML.stringify(data).trimEnd()}\n${fence}`;
}
//# sourceMappingURL=props.js.map